import React, { useEffect, useMemo, useState } from 'react';

import { Button, Modal, SearchableDropdown } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { scheduledInstantAPI } from '@/services/api/scheduled-instant';

import { SI_TAG } from './riderFilter';
import { useDriversDropdown } from './useDriversDropdown';

interface DayOffEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Edit mode: a fixed rider. Add mode: null (shows a rider search). */
  rider?: { code: string; name: string } | null;
}

const DAYS = [
  { value: 1, label: 'Sen' },
  { value: 2, label: 'Sel' },
  { value: 3, label: 'Rab' },
  { value: 4, label: 'Kam' },
  { value: 5, label: 'Jum' },
  { value: 6, label: 'Sab' },
  { value: 7, label: 'Min' },
];

const labelCls =
  'mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500';

const DayOffEditorModal: React.FC<DayOffEditorModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  rider,
}) => {
  const isEdit = !!rider;
  // Only dedicated riders (tagged "Schedule Instant") can have a day-off set.
  const drivers = useDriversDropdown(isOpen && !isEdit, {
    tagNames: SI_TAG.DEDICATED,
  });
  const [code, setCode] = useState('');
  const [riderName, setRiderName] = useState('');
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [newDate, setNewDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const lookup = async (riderCode: string) => {
    setLoading(true);
    try {
      const d = await scheduledInstantAPI.getDayOff(riderCode);
      setWeekdays(d?.recurringWeekdays ?? []);
      setDates(d?.specificDates ?? []);
    } catch (e: any) {
      showErrorToast('Gagal', e.message || 'Gagal memuat day-off');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setNewDate('');
    if (rider) {
      setCode(rider.code);
      setRiderName(rider.name);
      setSelectedId('');
      lookup(rider.code);
    } else {
      setCode('');
      setRiderName('');
      setSelectedId('');
      setWeekdays([]);
      setDates([]);
    }
  }, [isOpen, rider]);

  const pickRider = (id: string) => {
    const d = drivers.options.find((o) => String(o.id) === String(id));
    if (!d) return;
    const c = d.code || `DRV${d.id}`;
    const nm = d.name || `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim();
    setSelectedId(d.id);
    setCode(c);
    setRiderName(nm);
    lookup(c);
  };

  const toggleDay = (w: number) =>
    setWeekdays((prev) =>
      prev.includes(w)
        ? prev.filter((x) => x !== w)
        : [...prev, w].sort((a, b) => a - b)
    );
  const addDate = () => {
    if (newDate && !dates.includes(newDate)) {
      setDates((prev) => [...prev, newDate].sort());
      setNewDate('');
    }
  };
  const removeDate = (dt: string) =>
    setDates((prev) => prev.filter((x) => x !== dt));

  const validation = useMemo(() => {
    if (!code) return 'Pilih rider dulu.';
    return null;
  }, [code]);

  const save = async () => {
    if (!code) return;
    setSaving(true);
    try {
      await scheduledInstantAPI.setDayOff(code, {
        // Snapshot the name so the list doesn't need a per-rider directory
        // lookup to display it (avoids the N+1 on the Day-Off tab).
        riderName: riderName || undefined,
        recurringWeekdays: weekdays,
        specificDates: dates,
      });
      showSuccessToast('Day-off disimpan', riderName || code);
      onSaved();
      onClose();
    } catch (e: any) {
      showErrorToast('Gagal menyimpan', e.message || 'Terjadi kesalahan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !saving && onClose()}
      icon="ri-calendar-close-line"
      iconTone="primary"
      title={isEdit ? 'Atur Day-Off' : 'Tambah Day-Off Rider'}
      subtitle={
        isEdit
          ? `${riderName} · ${code}`
          : 'Pilih rider, lalu tandai hari libur rutin & tanggal spesifik'
      }
      width="md"
      closeOnBackdropClick={!saving}
      closeOnEscape={!saving}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={saving}>
            Batal
          </Button>
          <Button onClick={save} disabled={saving || !!validation}>
            {saving ? 'Menyimpan…' : 'Simpan Day-Off'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {!isEdit && (
          <div>
            <label className={labelCls}>
              Rider <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={drivers.options.map((d) => ({
                value: d.id,
                label: `${
                  d.name || `${d.first_name ?? ''} ${d.last_name ?? ''}`.trim()
                }${d.code ? ` · ${d.code}` : ''}`,
              }))}
              value={selectedId === '' ? undefined : selectedId}
              selectedLabelFallback={riderName}
              onChange={(next) => next && pickRider(String(next))}
              onSearchChange={drivers.setSearch}
              disableLocalFilter
              loading={drivers.loading}
              hasMore={drivers.hasMore}
              onLoadMore={drivers.loadMore}
              placeholder="Cari rider — nama, kode, atau telepon…"
              emptyMessage={drivers.error || 'Rider tidak ditemukan'}
            />
          </div>
        )}

        <div>
          <label className={labelCls}>
            Hari Libur Rutin{loading && ' · memuat…'}
          </label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => {
              const active = weekdays.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => toggleDay(d.value)}
                  disabled={saving || (!code && !isEdit)}
                  className={`px-3 py-1.5 rounded-gsm text-xs font-semibold border transition-colors disabled:opacity-40 ${
                    active
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-primary hover:text-primary'
                  }`}
                  aria-pressed={active}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className={labelCls}>Tanggal Off Spesifik</label>
          <div className="flex gap-2">
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              disabled={saving || (!code && !isEdit)}
              className="rounded-gsm border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 font-mono focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100"
            />
            <Button
              variant="outline-accent"
              size="small"
              onClick={addDate}
              disabled={!newDate || saving}
            >
              Tambah
            </Button>
          </div>
          {dates.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {dates.map((dt) => (
                <span
                  key={dt}
                  className="inline-flex items-center gap-1 bg-tint border border-gray-200 rounded-gsm px-2 py-1 text-[11px] font-mono text-ink"
                >
                  {dt}
                  <button
                    type="button"
                    onClick={() => removeDate(dt)}
                    disabled={saving}
                    className="text-gray-400 hover:text-red-600"
                    aria-label={`Hapus ${dt}`}
                  >
                    <i className="ri-close-line" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 italic mt-2">
              Belum ada tanggal spesifik.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DayOffEditorModal;
